import {ChangeDetectorRef, Component, computed, inject, OnDestroy, OnInit, signal} from '@angular/core';
import {HEATMAP_CENTER_KEY, HEATMAP_ZOOM_KEY, MAP_CENTER_KEY, MAP_ZOOM_KEY} from '@shared/constants/storage-keys';
import {CommonModule} from '@angular/common';
import {ActivatedRoute, NavigationEnd, Router, RouterModule} from '@angular/router';
import {filter} from 'rxjs/operators';
import {DemandaService} from '@core/services/demanda.service';
import {MapaDemandasService, PinMapa} from '@core/services/mapa-demandas.service';
import {TipoObraService} from '@core/services/tipo-obra.service';
import {
  HeaderBotoesFlutuantesMapa
} from '@pages/demandas/mapa-demandas/header-botoes-flutuantes-mapa/header-botoes-flutuantes-mapa.component';
import {Subscription} from 'rxjs';
import {GoogleMapsLoaderService} from '@core/services/google-maps-loader.service';
import {GoogleMap, MapAdvancedMarker} from '@angular/google-maps';
import {criarMapaCalorOverlay, MapaCalorOverlay} from './mapa-calor-overlay';
import {environment} from '@environments/environment';
import {ToastModule} from 'primeng/toast';
import {toSignal} from '@angular/core/rxjs-interop';
import {
  CadastroRepresentacaoUserGovbrComponent
} from '@pages/demandas/cadastro-representacao-user-govbr/cadastro-representacao-user-govbr.component';
import {
  InputSelectTipoObra
} from '@pages/demandas/adicionar-demanda/input-select-tipo-obra/input-select-tipo-obra.component';
import {
  DetalhesDemandaDialogComponent
} from '@pages/demandas/mapa-demandas/detalhes-demanda-dialog/detalhes-demanda-dialog.component';
import {Demanda} from '@core/models/demanda';

@Component({
  selector: 'app-mapa-demandas',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    GoogleMap,
    MapAdvancedMarker,
    HeaderBotoesFlutuantesMapa,
    ToastModule,
    CadastroRepresentacaoUserGovbrComponent,
    InputSelectTipoObra,
    DetalhesDemandaDialogComponent,
  ],
  templateUrl: './mapa-demandas.component.html',
  styleUrl: './mapa-demandas.component.css',
})
export class MapaDemandasComponent implements OnInit, OnDestroy {
  protected readonly mapId = environment.google.mapId;
  protected readonly mapaDemandasService = inject(MapaDemandasService);
  private readonly tipoObraService = inject(TipoObraService);

  protected readonly isMapaCarregado = signal(false);
  protected showMapa = true;
  protected showMapaCalor = false;
  protected showFilterTipoObra = false;

  private readonly pins = toSignal(this.mapaDemandasService.pins$, {initialValue: []});
  protected readonly selectedTipoObraId = signal<number | null>(null);

  protected readonly pinsFiltrados = computed(() => {
    this.tipoObraService.iconesCarregados();
    const filtro = this.selectedTipoObraId();
    const pins = filtro
      ? this.pins().filter(p => p.idTipoObra === filtro)
      : this.pins();
    return pins.map(p => ({...p, content: this.tipoObraService.buildPinIcon(p.idTipoObra)}));
  });

  protected zoom = 8;
  protected center: google.maps.LatLngLiteral = {lat: -23.5505, lng: -46.6333};
  protected mapOptions: google.maps.MapOptions = {};

  protected heatmapZoom = 7;
  protected heatmapCenter: google.maps.LatLngLiteral = {lat: -23.5505, lng: -46.6333};
  protected heatmapMapOptions: google.maps.MapOptions = {};

  private heatmapOverlay?: MapaCalorOverlay;

  protected showDetalhesDemandaDialog = false;
  protected showCadastroRepresentacaoGovbr: boolean = false;
  protected demandasDaLocalizacaoSelecionada: Demanda[] = [];
  protected loadingDetalhesDemanda = false;

  private readonly subscriptions = new Subscription();

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly googleMapsLoader: GoogleMapsLoaderService,
    private readonly demandaService: DemandaService,
    private readonly cdr: ChangeDetectorRef,
  ) {
  }

  ngOnInit(): void {
    this.showCadastroRepresentacaoGovbr = !!this.route.snapshot.data['showCadastroTipoRepresentacao'];
    this.restoreZoomAndCenter();
    this.showMapa = this.isMapRoute(this.router.url);
    this.showMapaCalor = this.isMapaCalorRoute(this.router.url);
    this.showFilterTipoObra = this.showMapa || this.showMapaCalor;
    if (this.showMapa || this.showMapaCalor) {
      this.carregarMapa();
    }
    if (this.showMapa) {
      this.fetchDemandas();
    }
    this.subscribeToRouterEvents();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.heatmapOverlay?.remove();
  }

  onMapInitialized(map: google.maps.Map): void {
    map.addListener('zoom_changed', () => this.persistZoom(map.getZoom() ?? this.zoom));
    map.addListener('center_changed', () => {
      const center = map.getCenter();
      if (center) this.persistCenter(center.toJSON());
    });
    this.mapOptions = this.mapaDemandasService.buildMapOptions();
    this.cdr.detectChanges();
    this.mapaDemandasService.drawContornoSaoPaulo(map);
  }

  onMapaCalorInitialized(map: google.maps.Map): void {
    map.addListener('zoom_changed', () => this.persistHeatmapZoom(map.getZoom() ?? this.heatmapZoom));
    map.addListener('center_changed', () => {
      const center = map.getCenter();
      if (center) this.persistHeatmapCenter(center.toJSON());
    });
    this.heatmapMapOptions = this.mapaDemandasService.buildMapOptions();
    this.cdr.detectChanges();
    this.mapaDemandasService.drawContornoSaoPaulo(map);

    this.heatmapOverlay = criarMapaCalorOverlay(map, {radius: 15, opacity: 0.9});
    this.subscriptions.add(
      this.mapaDemandasService.heatmap$.subscribe(literais => {
        const data = literais.map(l => new google.maps.LatLng(l.lat, l.lng));
        this.heatmapOverlay?.setData(data);
      })
    );
  }

  onFilterByTipoObra(idTipoObra: number | null): void {
    this.selectedTipoObraId.set(idTipoObra ?? null);
  }

  onClickPinDemanda(pin: PinMapa): void {
    this.demandasDaLocalizacaoSelecionada = [];
    this.loadingDetalhesDemanda = true;
    this.showDetalhesDemandaDialog = true;

    this.demandaService.listarDemandasPorLocalizacao(pin.position.lat, pin.position.lng).subscribe({
      next: (response) => {
        this.demandasDaLocalizacaoSelecionada = response;
        this.loadingDetalhesDemanda = false;
      },
      error: (error) => {
        console.error('Erro ao carregar demandas por localização:', error);
        this.loadingDetalhesDemanda = false;
      }
    });
  }

  private carregarMapa(): void {
    this.googleMapsLoader.load().then(() => {
      this.isMapaCarregado.set(true);
    });
  }

  private fetchDemandas(): void {
    this.mapaDemandasService.loadDemandas();
  }

  private subscribeToRouterEvents(): void {
    this.subscriptions.add(
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd)
      ).subscribe((event: NavigationEnd) => {
        const wasMapRouteActive = this.showMapa;
        this.restoreZoomAndCenter();
        this.showMapa = this.isMapRoute(event.urlAfterRedirects);
        this.showMapaCalor = this.isMapaCalorRoute(event.urlAfterRedirects);
        this.showFilterTipoObra = this.showMapa || this.showMapaCalor;
        if ((this.showMapa || this.showMapaCalor) && !this.isMapaCarregado()) {
          this.carregarMapa();
        } else if (this.showMapa && wasMapRouteActive) {
          this.fetchDemandas();
        }
      })
    );
  }

  private restoreZoomAndCenter(): void {
    this.restoreStoredZoom();
    this.restoreStoredCenter();
    this.restoreStoredHeatmapZoom();
    this.restoreStoredHeatmapCenter();
  }

  private restoreStoredZoom(): void {
    const stored = sessionStorage.getItem(MAP_ZOOM_KEY);
    if (stored) this.zoom = Number.parseInt(stored);
  }

  private persistZoom(zoom: number): void {
    this.zoom = zoom;
    sessionStorage.setItem(MAP_ZOOM_KEY, zoom.toString());
  }

  private restoreStoredCenter(): void {
    const stored = sessionStorage.getItem(MAP_CENTER_KEY);
    if (stored) this.center = JSON.parse(stored);
  }

  private persistCenter(center: google.maps.LatLngLiteral): void {
    sessionStorage.setItem(MAP_CENTER_KEY, JSON.stringify(center));
  }

  private restoreStoredHeatmapZoom(): void {
    const stored = sessionStorage.getItem(HEATMAP_ZOOM_KEY);
    if (stored) this.heatmapZoom = Number.parseInt(stored);
  }

  private persistHeatmapZoom(zoom: number): void {
    this.heatmapZoom = zoom;
    sessionStorage.setItem(HEATMAP_ZOOM_KEY, zoom.toString());
  }

  private restoreStoredHeatmapCenter(): void {
    const stored = sessionStorage.getItem(HEATMAP_CENTER_KEY);
    if (stored) this.heatmapCenter = JSON.parse(stored);
  }

  private persistHeatmapCenter(center: google.maps.LatLngLiteral): void {
    sessionStorage.setItem(HEATMAP_CENTER_KEY, JSON.stringify(center));
  }

  private isMapRoute(url: string): boolean {
    return url === '/demandas/mapa' || url === '/cadastro-tipo-representacao-govbr';
  }

  private isMapaCalorRoute(url: string): boolean {
    return url === '/demandas/mapa-calor';
  }
}
